import type React from "react";
import { useEffect, useRef } from "react";
import { productMeta, type Product } from "../../sim/payments";
import { FLOWER_IDS, flower, type FlowerId } from "../../sim/flowers";
import { DECOR_IDS } from "../../sim/decor";
import { PetalsCoin } from "./PetalsCoin";

/**
 * A PRODUCT'S HERO SHOT — the one picture both shops show for a product: the website's grid and product page
 * (ui/site/ProductCard, ProductDetailModal) and the game's Premium Shop (ui/PremiumShopModal). One component,
 * so the two shops can never drift apart in how a product looks.
 *
 * THE PANEL. Not a little outdoor scene any more: the old sky-and-grass window read as a placeholder. The
 * asset now floats over a bold patterned panel, the way a mobile game presents a shop item:
 *   • a tinted radial ground, lit from just above centre and dark at the rim;
 *   • slow rotating sunburst rays behind the item (CSS, compositor-only; still under reduced motion);
 *   • a repeating motif that says what KIND of thing it is: leaves for plants, a diamond lattice for decor,
 *     sparkles for everything else (Petal packs, perks). It is masked away from the centre so it never
 *     competes with the asset;
 *   • a soft vignette and an inner rim highlight.
 * The TINT is the product's own: a plant takes its species' petal colour, decor a warm amber, anything else
 * the Petals berry. An admin can override it with the product's accent colour.
 *
 * THE ASSET. A granted plant or decor piece is drawn live by render/pixi/showcase (one shared renderer for
 * the whole page; loaded lazily, so a page without a live product never downloads Pixi). Anything else shows
 * the product's first gallery image, else a Petals coin.
 */
export type HeroKind = "plant" | "decor" | "other";

export function heroAssetId(product: Product): string | null {
  const ref = product.grant && product.grant.type !== "none" ? product.grant.refId : null;
  if (!ref) return null;
  return (FLOWER_IDS as readonly string[]).includes(ref) || (DECOR_IDS as readonly string[]).includes(ref) ? ref : null;
}

/** The granted plant's display name, for productCopy (undefined for anything that is not a known plant). */
export function heroPlantName(product: Product): string | undefined {
  const ref = heroAssetId(product);
  return ref && (FLOWER_IDS as readonly string[]).includes(ref) ? flower(ref as FlowerId).name : undefined;
}

export function heroKind(product: Product): HeroKind {
  const ref = heroAssetId(product);
  if (ref && (FLOWER_IDS as readonly string[]).includes(ref)) return "plant";
  if (ref) return "decor";
  return "other";
}

/** The panel's tint: the admin's accent, else the species' petal colour, else the kind's family colour. */
function heroTint(product: Product, kind: HeroKind): string {
  const accent = productMeta(product).accent;
  if (accent) return accent;
  if (kind === "plant") {
    const petal = flower(heroAssetId(product) as FlowerId).petal;
    return `#${petal.toString(16).padStart(6, "0")}`;
  }
  return kind === "decor" ? "#e0a03c" : "#e2508f";
}

export function ProductHero({ product, className = "", children }: {
  product: Product;
  className?: string;
  /** overlays: badges, an Owned chip */
  children?: React.ReactNode;
}) {
  const kind = heroKind(product);
  const live = heroAssetId(product);
  const img = productMeta(product).images[0];
  const style = { "--ph-tint": heroTint(product, kind) } as React.CSSProperties;
  return (
    <div className={`ph-hero ph-${kind} ${className}`} style={style}>
      <div className="ph-rays" aria-hidden />
      <div className="ph-motif" aria-hidden />
      {live
        ? <LiveAsset assetId={live} seed={(product.id * 2654435761) % 1000000} />
        : img
          ? <img className="ph-img" src={img} alt={product.name} loading="lazy" draggable={false} />
          : <div className="ph-coin" aria-hidden><PetalsCoin size={72} /></div>}
      <div className="ph-vignette" aria-hidden />
      {children}
    </div>
  );
}

/** THE SAME PANEL FOR A PLANT ALONE — no product needed: the redeem reveal's hero (ui/RedeemReveal), so a plant a
 *  pin code unlocks is presented exactly as its Shop card presents it, live, in its own petal colour. */
export function PlantHero({ plantId, className = "", children }: { plantId: FlowerId; className?: string; children?: React.ReactNode }) {
  const petal = flower(plantId).petal;
  const style = { "--ph-tint": `#${petal.toString(16).padStart(6, "0")}` } as React.CSSProperties;
  let seed = 7;
  for (const ch of plantId) seed = (seed * 31 + ch.charCodeAt(0)) % 1000000;
  return (
    <div className={`ph-hero ph-plant ${className}`} style={style}>
      <div className="ph-rays" aria-hidden />
      <div className="ph-motif" aria-hidden />
      <LiveAsset assetId={plantId} seed={seed} />
      <div className="ph-vignette" aria-hidden />
      {children}
    </div>
  );
}

/** The live asset: a plain 2D canvas the shared showcase renderer copies its frames into. */
function LiveAsset({ assetId, seed }: { assetId: string; seed: number }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    let dead = false;
    let handle: { destroy(): void } | null = null;
    import("../../render/pixi/showcase").then((m) => {
      if (!dead && ref.current) handle = m.mountShowcase(ref.current, assetId, seed);
    }).catch(() => { /* chunk failed to load: the patterned panel still stands */ });
    return () => { dead = true; handle?.destroy(); };
  }, [assetId, seed]);
  return <canvas ref={ref} className="ph-canvas" aria-hidden />;
}
